import {AgeGroup, ClimbingEvent} from "../../../models/ClimbingEvent";
import {Sex} from "../../../models/UserData";
import {
    DifficultyCategory,
    getAllUserRouteStatus,
    StatusLabelAndColour,
    UserRouteStatus
} from "../../../models/UserClimbingEvent";
import {FC, useEffect, useRef, useState} from "react";
import {ClimbingEventSimplifiedResults, UserSumOfPoints} from "../../../models/ClimbingEventSimplifiedResults";
import {getSimplifiedResults} from "../../../services/events/EventSimplifiedResultService";
import {Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField} from "@mui/material";
import {Box, styled} from "@mui/system";
import {useTranslation} from "react-i18next";
import {ClimbingEventRoutesResults, UserRoutes} from "../../../models/ClimbingEventRoutesResults";
import {getRoutesResults} from "../../../services/events/EventRoutesResultService";
import {ResultRowComponent} from "./ResultRowComponent";

type Props = {
    climbingEvent: ClimbingEvent,
    sex?: Sex | undefined,
    difficulty?: DifficultyCategory,
    section: boolean | undefined,
    ageGroup?: AgeGroup,
    shouldScroll: boolean,
    userName: string | undefined
}

export type UserPointsAndPosition = {
    position: number,
    name: string | undefined,
    points: number,
    zones: number,
    group: string | undefined,
    userId: string
}

const HeaderCell = styled(TableCell)(({theme}) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
}));


export const ResultListComponent: FC<Props> = ({
                                                   climbingEvent,
                                                   sex,
                                                   difficulty,
                                                   section,
                                                   ageGroup,
                                                   shouldScroll,
                                                   userName
                                               }) => {
    const {t} = useTranslation();
    const [simplifiedResults, setSimplifiedResults] = useState<ClimbingEventSimplifiedResults[]>([])
    const [userRoutes, setUserRoutes] = useState<Map<string, UserRoutes>>(new Map<string, UserRoutes>())
    const [resultsToDisplay, setResultsToDisplay] = useState<UserPointsAndPosition[]>([])
    const [search, setSearch] = useState<string>('')
    const userRef = useRef(null)
    const [statuses, setStatuses] = useState<StatusLabelAndColour[]>([])
    const [routesToDisplay, setRoutesToDisplay] = useState<UserSumOfPoints[]>([])

    const isUserInAgeGroup = (ageGroup: AgeGroup, user: UserSumOfPoints): boolean => {
        console.log("is user in age group", ageGroup, user)
        if (user.birthYear) {
            if (ageGroup.endYear) {
                return user.birthYear <= ageGroup.endYear && (!ageGroup.startYear || user.birthYear >= ageGroup.startYear)
            } else {
                return !!ageGroup.startYear && user.birthYear >= ageGroup.startYear
            }
        }
        return false
    }

    useEffect(() => {
        getSimplifiedResults(climbingEvent.name).then((results) => {
            setSimplifiedResults(results)
        }).then(_ => {
            if (climbingEvent.showRoutesForUsers) {
                getRoutesResults(climbingEvent.name)
                    .then(r => {
                        const mergedMap = new Map<string, UserRoutes>();
                        r.filter(v => sex === undefined || v.sex === sex)
                            .forEach(v => v.userRoutes.forEach((value, key) => {
                                mergedMap.set(key, value)
                            }))
                        setUserRoutes(mergedMap)
                    })
                    .then(_ => setStatuses(getAllUserRouteStatus(climbingEvent).filter(s => s.status !== UserRouteStatus.NOTHING)))
            }
        })
    }, [climbingEvent])

    useEffect(() => {
        if (sex === undefined) {
            const results = simplifiedResults?.flatMap(sr => Array.from(sr.userSumOfPoints.values()));
            setRoutesToDisplay(results || [])
        } else {
            const results = simplifiedResults?.find(sr => sex === undefined || sr.sex === sex)
                ?.userSumOfPoints.values()
            setRoutesToDisplay(Array.from(results || []))
        }

    }, [simplifiedResults, sex]);

    useEffect(() => {
        const sortedUsers = routesToDisplay
            .filter(r => difficulty === undefined || r.difficultyCategory === difficulty)
            .filter(r => section === undefined || r.section === section)
            .sort((u1, u2) => {
                const pointsDiff = u2.points - u1.points
                if (pointsDiff !== 0) {
                    return pointsDiff
                } else {
                    return (u2.zones || 0) - (u1.zones || 0)
                }
            }).filter((u) => !ageGroup || isUserInAgeGroup(ageGroup, u))
        let position = 1
        const resultsWithPosition = sortedUsers.map((r, index) => {
            const result = {
                position,
                name: r.name,
                points: r.points,
                zones: r.zones || 0,
                group: r.group,
                userId: r.userId
            }
            if (!(sortedUsers[index + 1]?.points === r.points && (!(climbingEvent.separateZonePoint || climbingEvent.separateFlashPoint) || sortedUsers[index + 1]?.zones === r.zones))) {
                position = index + 2
            }
            return result
        });
        setResultsToDisplay(resultsWithPosition)
        console.log(resultsWithPosition)
    }, [sex, routesToDisplay, difficulty, climbingEvent, section, ageGroup])


    const scroll = () => {
        if (userRef.current) {
            // @ts-ignore
            userRef.current.scrollIntoView({behavior: 'smooth', block: 'center'})
        }
    }
    useEffect(() => {
        if (shouldScroll) {
            scroll()
        }
    })

    return (
        <Box display={'flex'} flexDirection={"column"} gap={2}>
            <TextField
                sx={{maxWidth: '90lvw', alignSelf: 'center', mt: 2, minWidth: 250}}
                label={t('results.search')}
                variant={"outlined"}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            <Paper sx={{width: '90lvw', overflow: 'hidden', m: 2}}>
                <Table sx={{width: '100%'}} aria-label={t('results.label')}>
                    <TableHead>
                        <TableRow>
                            <HeaderCell>{t('results.place')}</HeaderCell>
                            <HeaderCell>{t('results.name')}</HeaderCell>
                            <HeaderCell>{t('results.points')}</HeaderCell>
                            {climbingEvent?.separateZonePoint &&
                                <HeaderCell>{t('results.zones')}</HeaderCell>
                            }
                            {climbingEvent?.separateFlashPoint &&
                                <HeaderCell>{t('results.flashes')}</HeaderCell>
                            }
                            <HeaderCell>{t('results.more')}</HeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {resultsToDisplay
                            .filter(r => r.name?.toLowerCase().includes(search.toLowerCase()))
                            .map((result) => (<>
                                <ResultRowComponent result={result}
                                                    isCurrentUser={userName === result.name}
                                                    separateZonePoints={climbingEvent?.separateZonePoint || climbingEvent?.separateFlashPoint}
                                                    statuses={statuses}
                                                    routes={userRoutes?.get(result.userId)}/>
                                {userName === result.name && <p ref={userRef}></p>}
                            </>))}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    )
}